import { apiClient } from './apiClient';

// ============================================
// TIPOS
// ============================================

export type UsagePeriod = 'today' | 'week' | 'month' | 'all';

export interface UsageTotals {
  tokens: number;
  promptTokens: number;
  completionTokens: number;
  requests: number;
  activeUsers?: number;
  costUsd: number;
  costIsEstimated: boolean;
}

export interface UsageByModel {
  model: string;
  tokens: number;
  promptTokens: number;
  completionTokens: number;
  requests: number;
  costUsd: number;
}

export interface UsageByService {
  service: string;
  tokens: number;
  requests: number;
  costUsd: number;
}

export interface UsageTopUser {
  userId: string;
  name: string;
  email: string;
  tokens: number;
  requests: number;
  costUsd: number;
}

export interface DailyTrend {
  day: string;
  tokens: number;
  requests: number;
}

export interface UsageOverview {
  period: string;
  totals: UsageTotals;
  byModel: UsageByModel[];
  byService: UsageByService[];
  topUsers: UsageTopUser[];
  dailyTrend: DailyTrend[];
  modelPricing: Record<string, { input: number; output: number }>;
}

export interface UserUsageBreakdown {
  service: string;
  model: string;
  tokens: number;
  requests: number;
  costUsd: number;
  lastUsed: string;
}

export interface UserUsageData {
  userId: string;
  period: string;
  totals: UsageTotals;
  breakdown: UserUsageBreakdown[];
  dailyTrend: DailyTrend[];
}

// ---- Billing real (OpenAI Admin API) ----

export interface BillingDailyCost {
  date: string;
  costUsd: number;
  byModel: { name: string; costUsd: number }[];
}

export interface BillingBudget {
  monthlyUsd: number;
  usedUsd: number;
  remainingUsd: number;
  pctUsed: number;
}

export interface BillingReconciliation {
  openaiTokens: number;
  trackedTokens: number;
  coveragePct: number;
}

export interface OpenAiBillingData {
  configured: boolean;
  period: { startDate: string; endDate: string };
  currentMonth: {
    totalCostUsd: number | null;
    dailyCosts: BillingDailyCost[];
  };
  budget: BillingBudget | null;
  reconciliation: BillingReconciliation | null;
  error?: string;
}

// ---- Spend alerts (OpenAI Admin API) ----

export interface SpendAlert {
  id: string;
  thresholdUsd: number;
  interval: string;
  recipients: string[];
}

export interface SpendAlertProject {
  id: string;
  name: string;
  alerts: SpendAlert[];
}

export interface SpendAlertsData {
  configured: boolean;
  projects: SpendAlertProject[];
  error?: string;
}

// ============================================
// SERVIÇO
// ============================================

class UsageService {
  /**
   * Busca métricas globais de uso (admin)
   */
  async getUsageOverview(period: UsagePeriod = 'month'): Promise<UsageOverview> {
    const response = await apiClient.get<UsageOverview>('/auth/stats/usage-overview', {
      params: { period },
    });
    return response.data;
  }

  /**
   * Busca uso de um usuário específico (admin)
   */
  async getUserUsage(userId: string, period: UsagePeriod = 'month'): Promise<UserUsageData> {
    const response = await apiClient.get<UserUsageData>(`/auth/stats/user/${userId}/usage`, {
      params: { period },
    });
    return response.data;
  }

  /**
   * Busca dados reais de billing da OpenAI (admin key required)
   */
  async getBillingData(refresh = false): Promise<OpenAiBillingData> {
    const response = await apiClient.get<OpenAiBillingData>('/auth/stats/openai-billing', {
      params: refresh ? { refresh: 'true' } : undefined,
    });
    return response.data;
  }

  /**
   * Atualiza o orçamento mensal de OpenAI (USD)
   */
  async setMonthlyBudget(usd: number): Promise<void> {
    await apiClient.put('/auth/settings/openai_monthly_budget_usd', { value: String(usd) });
  }

  /**
   * Lista projetos OpenAI e seus spend alerts
   */
  async getSpendAlerts(): Promise<SpendAlertsData> {
    const response = await apiClient.get<SpendAlertsData>('/auth/stats/openai-spend-alerts');
    return response.data;
  }

  /**
   * Cria spend alert em um projeto OpenAI
   */
  async createSpendAlert(projectId: string, thresholdUsd: number, recipients: string[]): Promise<SpendAlert> {
    const response = await apiClient.post<{ alert: SpendAlert }>('/auth/stats/openai-spend-alerts', {
      projectId,
      thresholdUsd,
      recipients,
    });
    return response.data.alert;
  }

  /**
   * Remove spend alert de um projeto OpenAI
   */
  async deleteSpendAlert(projectId: string, alertId: string): Promise<void> {
    await apiClient.delete(`/auth/stats/openai-spend-alerts/${projectId}/${alertId}`);
  }
}

export const usageService = new UsageService();
